import { useState, useEffect, useMemo } from 'react';
import { MainLayout } from '@templates/index';
import { RestaurantGrid, CuisineCategories, HeroSection, CitySelector } from '@organisms/index';
import { Button, Icon } from '@atoms/index';
import { catalogService } from '@services/catalogService';
import { getCuisineFilterItems, type CuisineFilterItem } from '@services/cuisineService';
import { geoService } from '@services/geoService';
import { geocodingService } from '@services/geocodingService';
import { addressService } from '@services/addressService';
import { useAuthStore } from '@store/authStore';
import { useCartStore } from '@store/cartStore';
import { useLocationStore } from '@store/locationStore';
import { storage, STORAGE_KEYS, LOCATION_CACHE_TTL } from '@utils/storage';
import type { Restaurant, ServiceZone, UserAddress } from '@/types';

interface CachedServiceZone {
  zone: ServiceZone;
  timestamp: number;
}

interface CachedLocation {
  latitude: number;
  longitude: number;
  timestamp: number;
}

const HomePage = () => {
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [serviceZone, setServiceZone] = useState<ServiceZone | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCuisine, setSelectedCuisine] = useState<string>('all');
  const [cuisineFilters, setCuisineFilters] = useState<CuisineFilterItem[]>([]);
  const [showOnlyOpen, setShowOnlyOpen] = useState(false);

  const { isAuthenticated } = useAuthStore();
  const { setServiceZone: setCartServiceZone } = useCartStore();
  const {
    serviceZone: locationServiceZone,
    setServiceZone: setLocationServiceZone,
    setSelectedCity,
    selectedCity,
    activeAddress,
    loadFromStorage,
  } = useLocationStore();

  useEffect(() => {
    loadFromStorage();
  }, [loadFromStorage]);

  useEffect(() => {
    initializeServiceZone();
  }, [isAuthenticated]);

  useEffect(() => {
    getCuisineFilterItems()
      .then(setCuisineFilters)
      .catch(() => setCuisineFilters([]));
  }, []);

  useEffect(() => {
    if (selectedCuisine === 'all') return;
    const exists = cuisineFilters.some((cuisine) => cuisine.id === selectedCuisine);
    if (!exists) {
      setSelectedCuisine('all');
    }
  }, [cuisineFilters, selectedCuisine]);

  useEffect(() => {
    if (locationServiceZone?.id) {
      setServiceZone(locationServiceZone);
      loadRestaurants(locationServiceZone.id, undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationServiceZone]);

  useEffect(() => {
    if (selectedCity?.id) {
      setServiceZone(null);
      loadRestaurants(undefined, selectedCity.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCity]);

  const initializeServiceZone = async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (isAuthenticated) {
        try {
          const addresses = await addressService.getAddresses();
          const defaultAddress = addresses.find((addr) => addr.isDefault) || addresses[0];
          
          if (defaultAddress && defaultAddress.location) {
            const location = defaultAddress.location as { type: string; coordinates: number[] };
            const [longitude, latitude] = location.coordinates;
            
            console.log(`📍 Using saved address: ${defaultAddress.title} (lat=${latitude}, lon=${longitude})`);
            
            const zone = await geoService.checkServiceZone(latitude, longitude);
            if (zone) {
              setServiceZone(zone);
              setLocationServiceZone(zone);
              if (isAuthenticated) {
                await setCartServiceZone(zone.id).catch(err => {
                  console.error('Failed to set service zone in cart:', err);
                });
              }
              await loadRestaurants(zone.id, undefined);

              const zoneData: CachedServiceZone = {
                zone,
                timestamp: Date.now(),
              };
              storage.set(STORAGE_KEYS.SERVICE_ZONE_DATA, zoneData);
              storage.set(STORAGE_KEYS.LAST_SERVICE_ZONE, zone.id);
              setIsLoading(false);
              return;
            } else {
              console.warn('⚠️ Saved address is outside service zones, falling back to geolocation');
            }
          }
        } catch (err) {
          console.error('Failed to load saved addresses:', err);
        }
      }

      const cachedZoneData = storage.get<CachedServiceZone>(STORAGE_KEYS.SERVICE_ZONE_DATA);
      
      if (cachedZoneData && cachedZoneData.zone) {
        const now = Date.now();
        const cacheAge = now - cachedZoneData.timestamp;

        if (cacheAge < LOCATION_CACHE_TTL) {
          setServiceZone(cachedZoneData.zone);
          setLocationServiceZone(cachedZoneData.zone);
          if (isAuthenticated) {
            await setCartServiceZone(cachedZoneData.zone.id).catch(err => {
              console.error('Failed to set service zone in cart:', err);
            });
          }
          await loadRestaurants(cachedZoneData.zone.id, undefined);
          setIsLoading(false);
          return;
        } else {
          storage.remove(STORAGE_KEYS.SERVICE_ZONE_DATA);
          storage.remove(STORAGE_KEYS.USER_LOCATION);
        }
      }

      const lastZoneId = storage.get<string>(STORAGE_KEYS.LAST_SERVICE_ZONE);
      if (lastZoneId) {
        const cachedZone = storage.get<CachedServiceZone>(STORAGE_KEYS.SERVICE_ZONE_DATA);
        if (cachedZone?.zone) {
          setServiceZone(cachedZone.zone);
          setLocationServiceZone(cachedZone.zone);
          if (isAuthenticated) {
            await setCartServiceZone(cachedZone.zone.id).catch(err => {
              console.error('Failed to set service zone in cart:', err);
            });
          }
        }
        await loadRestaurants(lastZoneId, undefined);
      }
    } catch (err) {
      console.error('Failed to initialize service zone:', err);
      setError('Konum bilgisi yüklenirken bir hata oluştu.');
    } finally {
      setIsLoading(false);
    }
  };

  const deliveryCoordinates = activeAddress
    ? { latitude: activeAddress.latitude, longitude: activeAddress.longitude }
    : undefined;

  const loadRestaurants = async (zoneId?: string, cityId?: string) => {
    if (!zoneId && !cityId) {
      setError(zoneId === undefined && cityId === undefined ? 'Konum veya şehir seçin.' : 'Service zone bulunamadı.');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const data = await catalogService.getRestaurants(zoneId, cityId, undefined, deliveryCoordinates);
      setRestaurants(Array.isArray(data) ? data : []);
      if (zoneId) storage.set(STORAGE_KEYS.LAST_SERVICE_ZONE, zoneId);
    } catch (err) {
      setError('Restoranlar yüklenirken bir hata oluştu.');
      console.error('Load restaurants error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredRestaurants = useMemo(() => {
    let list = restaurants ?? [];

    if (showOnlyOpen) {
      list = list.filter((r) => r.isOpen === true);
    }

    if (selectedCuisine === 'all') {
      return list;
    }

    return list.filter((restaurant) => {
      const cuisineIds = restaurant.cuisineCategoryIds?.length
        ? restaurant.cuisineCategoryIds
        : restaurant.cuisineCategoryId
          ? [restaurant.cuisineCategoryId]
          : [];
      return cuisineIds.includes(selectedCuisine);
    });
  }, [restaurants, selectedCuisine, showOnlyOpen]);

  const handleLocationRequest = async () => {
    if (!navigator.geolocation) {
      setError('Tarayıcınız konum servisini desteklemiyor.');
      return;
    }

    setIsLoading(true);
    setError(null);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;

          const isDevelopment = import.meta.env.DEV || import.meta.env.MODE === 'development';
          const finalLat = isDevelopment ? 37.0000 : latitude;
          const finalLon = isDevelopment ? 35.3213 : longitude;
          
          console.log(`🌍 Checking zone for: lat=${finalLat}, lon=${finalLon}${isDevelopment ? ' (dev mode - using test coordinates)' : ''}`);

          let zone = await geoService.checkServiceZone(finalLat, finalLon);
          if (!zone) {
            const rev = await geocodingService.reverseGeocode(finalLat, finalLon);
            if (rev?.city?.trim()) {
              const cityZones = await geoService.getZonesByCity(rev.city);
              const districtNorm = (rev.district ?? '').trim().toLowerCase();
              zone =
                cityZones.find((z) => z.district?.trim().toLowerCase() === districtNorm) ??
                cityZones[0] ??
                null;
            }
          }
          console.log('✅ Zone result:', zone);

          if (zone) {
            setServiceZone(zone);
            setLocationServiceZone(zone);
            if (isAuthenticated) {
              await setCartServiceZone(zone.id).catch(err => {
                console.error('Failed to set service zone in cart:', err);
              });
            }

              const locationData: CachedLocation = {
              latitude: finalLat,
              longitude: finalLon,
              timestamp: Date.now(),
            };
            const zoneData: CachedServiceZone = {
              zone,
              timestamp: Date.now(),
            };
            
            storage.set(STORAGE_KEYS.USER_LOCATION, locationData);
            storage.set(STORAGE_KEYS.SERVICE_ZONE_DATA, zoneData);
            storage.set(STORAGE_KEYS.LAST_SERVICE_ZONE, zone.id);
            
            await loadRestaurants(zone.id, undefined);
          } else {
            setError(
              isDevelopment
                ? 'Üzgünüz, bu bölgede henüz hizmet vermiyoruz. (Development modu: Test koordinatları kullanılıyor)'
                : 'Üzgünüz, bu bölgede henüz hizmet vermiyoruz.'
            );
            setIsLoading(false);
          }
        } catch (err) {
          setError('Konum kontrolü yapılırken bir hata oluştu.');
          console.error('Location check error:', err);
          setIsLoading(false);
        }
      },
      (err) => {
        setError('Konum bilgisi alınamadı. Lütfen izinleri kontrol edin.');
        console.error('Geolocation error:', err);
        setIsLoading(false);
      }
    );
  };

  return (
    <MainLayout>
      <div className="relative left-1/2 -translate-x-1/2 w-screen">
        <HeroSection
          onLocationRequest={handleLocationRequest}
          isLoading={isLoading}
          hasServiceZone={!!serviceZone || !!selectedCity}
          serviceZoneId={locationServiceZone?.id ?? serviceZone?.id}
          deliveryLatitude={deliveryCoordinates?.latitude}
          deliveryLongitude={deliveryCoordinates?.longitude}
        />
      </div>

      {error && (
        <div className="bg-error/5 border border-error/20 rounded-2xl p-4 mb-8 animate-fade-in">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-error/10 flex items-center justify-center">
              <svg className="w-5 h-5 text-error" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="font-semibold text-gray-900 mb-1">Bir sorun oluştu</p>
              <p className="text-sm text-gray-600">{error}</p>
            </div>
          </div>
        </div>
      )}

      {(serviceZone || selectedCity) && (restaurants?.length ?? 0) > 0 && (
        <section className="animate-fade-in mb-12 mt-4 md:mt-8 bg-background">
          <CuisineCategories
            selectedCuisine={selectedCuisine}
            onSelectCuisine={setSelectedCuisine}
          />
        </section>
      )}

      {(serviceZone || selectedCity) && (
        <section className="animate-fade-in">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                {selectedCity ? `${selectedCity.name} Restoranları` : 'Tüm Restoranlar'} 🍽️
              </h2>
              <p className="text-gray-600">
                <span className="font-semibold text-primary">{filteredRestaurants?.length ?? 0}</span> restoran
                {selectedCuisine !== 'all' && (
                  <span className="ml-2">
                    ({cuisineFilters.find((c) => c.id === selectedCuisine)?.name})
                  </span>
                )}
                {showOnlyOpen && <span className="ml-2">(açık)</span>}
              </p>
            </div>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showOnlyOpen}
                onChange={(e) => setShowOnlyOpen(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className="text-sm font-medium text-gray-700">Sadece açık restoranlar</span>
            </label>
          </div>

          <RestaurantGrid
            restaurants={filteredRestaurants}
            isLoading={isLoading}
            onChangeLocation={() => {
              setSelectedCity(null);
              setLocationServiceZone(null);
              setServiceZone(null);
            }}
            onViewAllRestaurants={handleLocationRequest}
          />
        </section>
      )}

      {!serviceZone && !selectedCity && !isLoading && (
        <section className="text-center py-20 animate-fade-in">
          <div className="max-w-md mx-auto">
            <div className="inline-block p-6 rounded-3xl bg-gradient-primary/5 mb-6">
              <Icon name="location" size={64} className="text-primary" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              Restoranları Keşfet 🍽️
            </h3>
            <p className="text-gray-600 mb-8 leading-relaxed">
              Hizmet veren restoranları görmek için<br />
              lütfen konumunuzu paylaşın
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                size="lg"
                variant="primary"
                onClick={handleLocationRequest}
                className="shadow-premium"
              >
                <Icon name="location" size={20} className="mr-2" />
                📍 Konumumu Kullan
              </Button>
              <span className="text-gray-400 font-medium">veya</span>
              <div className="flex flex-col items-center gap-2">
                <span className="text-sm text-gray-600">Şehir seçerek restoranlara göz at</span>
                <CitySelector variant="inline" onCitySelect={() => {}} />
              </div>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-6 text-sm">
              <div className="text-center">
                <div className="text-3xl mb-2">⚡</div>
                <p className="font-semibold text-gray-900">Hızlı Teslimat</p>
              </div>
              <div className="text-center">
                <div className="text-3xl mb-2">🎯</div>
                <p className="font-semibold text-gray-900">Doğru Adres</p>
              </div>
              <div className="text-center">
                <div className="text-3xl mb-2">💳</div>
                <p className="font-semibold text-gray-900">Güvenli Ödeme</p>
              </div>
            </div>
          </div>
        </section>
      )}
    </MainLayout>
  );
};

export default HomePage;
